import { Component, inject } from '@angular/core';
import { CacTableComponent, table, TableBulkAction, TableClass, CacTableColumnSelectorComponent, CacFieldComponent } from '@controladad/ng-base';
import {
  joinNamesFn,
  joinArrayWithNullFn,
  toFixedFn,
  ProductTypeValues,
  ProductApprovalValues,
  FeaturedValues,
  KeepTopValues,
  ProductStatusValues,
  ProductApiServiceFactory,
  IProductApiService,
  cacTableIdCol,
  BrandApiService,
  AuthStore,
} from '@core';
import { ProductItem, CategoryItem } from '@models';
import { finalize, of } from 'rxjs';
import { switchMap, map, debounceTime, distinctUntilChanged } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { formControl } from '@al00x/forms';

@Component({
  selector: 'lib-admin-products',
  imports: [CacTableComponent, CacTableColumnSelectorComponent, CacFieldComponent],
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
})
export class ProductsComponent {
  private readonly productsApi: IProductApiService;
  private readonly brandApi = inject(BrandApiService);
  private readonly authStore = inject(AuthStore);

  admin = false;
  bulkActions: TableBulkAction<ProductItem>[] = [];
  route = inject(ActivatedRoute);
  table!: TableClass<ProductItem>;
  searchControl = formControl('');

  constructor(private factory: ProductApiServiceFactory) {
    this.admin = this.route.snapshot.data['isAdmin'] === true;
    this.productsApi = this.factory.getProductApiService(this.admin);

    this.initBulkActions();
    this.initTable();
    this.initSearchControl();
  }

  private initTable(): void {
    this.table = table({
      itemsFn: this.productsApi.getAdapter(),
      transformRequest: (request) => {
        return {
          ...request,
          filters: [
            ...(request.filters || []),
            {
              key: 'SearchQuery',
              value: this.searchControl.value,
              strictKey: false
            }
          ]

        };
      },
      clickableRows: true,
      view: { itemName: 'Product' },
      export: this.admin ? (filters) => this.productsApi.exportProducts(filters) : undefined,
      columns: {
        id: cacTableIdCol(),
        'thumbnail.url': { label: 'Thumbnail', type: 'image' },
        'name.english': {
          label: 'English Name',
          sortable: 'EnglishName',
          filterable: 'EnglishName',
          class: 'multiline-text',
        },
        'name.arabic': {
          label: 'Arabic Name',
          sortable: 'ArabicName',
          filterable: 'ArabicName',
          class: 'multiline-text',
        },
        categories: {
          label: 'Categories',
          transform: joinNamesFn('\n '),
          class: 'whitespace-pre',
          filterable: [
            {
              type: 'contains',
              controlType: 'select',
              multiple: true,
              key: 'Categories',
              items: this.getEndLayerCategories(),
            },
          ],
        },
        'brand.name.english': {
          label: 'Brand',
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              key: 'Brands',
              multiple: true,
              items: this.brandApi.getBrandItems(),
            },
          ],
        },
        vendorName: {
          hide: !this.admin,
          label: 'Vendor',
          sortable: 'Vendor',
          filterable: [
            {
              type: 'equal',
              label: 'Vendor Name',
              key: 'Vendors',
              multiple: true,
              controlType: 'select',
              items: this.productsApi.getAllVendors(),
            },
          ],
        },
        type: {
          label: 'Type',
          type: 'tag',
          items: ProductTypeValues,
          sortable: true,
          filterable: [{ type: 'equal', items: ProductTypeValues, controlType: 'select', key: 'Types', multiple: true }],
        },
        sku: {
          label: 'SKU',
          transform: joinArrayWithNullFn(),
          class: 'whitespace-pre',
          filterable: [
            {
              type: 'contains',
              key: 'Sku',
              multiple: true,
            },
          ],
        },
        stock: {
          label: 'Stock',
          transform: joinArrayWithNullFn(),
          class: 'whitespace-pre',
          sortable: true,
          filterable: [
            { type: 'greater', key: 'StockRangeFrom', inputType: 'number' },
            { type: 'lower', key: 'StockRangeTo', inputType: 'number' },
          ],
        },
        itemCode: {
          label: 'Item Code',
          transform: joinArrayWithNullFn(),
          class: 'whitespace-pre',
          filterable: [
            {
              type: 'contains',
              key: 'ItemCode',
              multiple: true,
            },
          ],
        },
        barcode: {
          label: 'Barcode',
          transform: joinArrayWithNullFn(),
          class: 'whitespace-pre',
          filterable: [
            {
              type: 'contains',
              key: 'Barcode',
              multiple: true,
            },
          ],
        },
        models: {
          label: 'Article',
          transform: joinArrayWithNullFn(),
          class: 'whitespace-pre',
          filterable: [
            {
              type: 'contains',
              key: 'Model',
              multiple: true,
            },
          ],
        },
        regularPrice: {
          label: 'Regular Price (KWD)',
          transform: (val: number[]) => {
            if (!val || val.length === 0) return '-';
            return val.map(v => toFixedFn(3)(v)).join('\n ');
          },
          class: 'whitespace-pre',
          filterable: [
            { type: 'greater', key: 'RegularPriceFrom', inputType: 'number' },
            { type: 'lower', key: 'RegularPriceTo', inputType: 'number' },
          ],
        },
        salePrice: {
          label: 'Sale Price (KWD)',
          transform: (val: (number | null)[]) => {
            if (!val || val.length === 0) return '-';
            return val.map(v => v === null ? '-' : toFixedFn(3)(v)).join('\n ');
          },
          class: 'whitespace-pre',
          filterable: [
            { type: 'greater', key: 'SalePriceFrom', inputType: 'number' },
            { type: 'lower', key: 'SalePriceTo', inputType: 'number' },
          ],
        },
        costPrice: {
          label: 'Cost Price (KWD)',
          transform: (val: number[]) => {
            if (!val || val.length === 0) return '-';
            return val.map(v => toFixedFn(3)(v)).join('\n ');
          },
          class: 'whitespace-pre',
          filterable: [
            { type: 'greater', key: 'CostPriceFrom', inputType: 'number' },
            { type: 'lower', key: 'CostPriceTo', inputType: 'number' },
          ],
        },
        isFeatured: {
          label: 'Is Featured',
          type: 'tag',
          sortable: true,
          items: FeaturedValues,
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              items: FeaturedValues,
              key: 'IsFeatured',
            },
          ],
        },
        keepTop: {
          label: 'Keep Top',
          type: 'tag',
          sortable: true,
          items: KeepTopValues,
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              items: KeepTopValues,
              key: 'KeepTop',
            },
          ],
        },
        status: {
          label: 'Status',
          type: 'tag',
          items: ProductStatusValues,
          sortable: true,
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              items: ProductStatusValues,
              key: 'Status',
              multiple: true,
            },
          ],
        },
        approvalStatus: {
          hide: !this.admin,
          label: 'Approval Status',
          type: 'tag',
          items: ProductApprovalValues,
          sortable: true,
          filterable: [
            {
              type: 'equal',
              controlType: 'select',
              label: 'Approval Status',
              items: ProductApprovalValues,
              key: 'ApprovalStatus',
              multiple: true,
            },
          ],
        },
        createdAt: {
          label: 'Created At',
          type: 'date',
          sortable: true,
        },
      },
      actions: [
        {
          type: 'icon',
          content: 'layers-linked',
          action: (product) => this.duplicateProduct(product),
        },
        {
          type: 'icon',
          content: 'edit',
          action: (product) => this.openProductDialog(product),
        },
        ...(!this.admin || !this.authStore.isRestrictedAdmin() ? [{
          type: 'icon' as const,
          content: 'trash',
          action: (item: ProductItem) => dialog$.deletePrompt(false, { itemName: item.name.english || `Product ${item.id}` }).action(() => this.productsApi.delete([item.id])),
        }] : []),
      ],
      bulkActions: this.bulkActions,
      events: {
        add: () =>
          dialog$
            .product({
              apiService: this.productsApi,
              isAdmin: this.admin,
            })
            .afterSubmit(),
      },
    });
  }

  onRowClick(row: any) {
    this.table.ref?.loading.set(true);

    this.openProductDialog(row.originalItem).pipe(
      finalize(() => {
        this.table.ref?.loading.set(false);
      })
    ).subscribe();
  }

  private openProductDialog(product: ProductItem) {
    return this.productsApi.get(product.id).pipe(
      switchMap(productEntity => {
        // Merge shareLink and approvalStatus from ProductItem to ProductEntity
        const enrichedEntity = {
          ...productEntity,
          shareLink: product.shareLink,
          approvalStatus: product.approvalStatus
        };

        return dialog$
          .product({
            apiService: this.productsApi,
            isAdmin: this.admin,
            product: enrichedEntity,
            productId: product.id,
          })
          .afterSubmit();
      })
    );
  }

  private duplicateProduct(product: ProductItem) {
    return this.productsApi.get(product.id).pipe(
      switchMap(productEntity => {
        // Set status to disabled (0) and clear all SKUs from variants
        const duplicatedProduct = {
          ...productEntity,
          status: 0,
          variants: productEntity.variants.map(variant => ({
            ...variant,
            sku: ''
          }))
        };

        return dialog$.product({
          apiService: this.productsApi,
          isAdmin: this.admin,
          product: duplicatedProduct,
          productId: product.id,
          duplicate: true,
        }).afterSubmit();
      })
    );
  }



  private initBulkActions(): void {
    this.bulkActions = [
      { content: 'Enable', action: this.bulkAction('status', true) },
      { content: 'Disable', action: this.bulkAction('status', false) },
    ];

    if (this.admin) {
      this.bulkActions.push(
        { content: 'Approve', action: this.bulkAction('approval', true) },
        { content: 'Un-Approve', action: this.bulkAction('approval', false) },
      );
    }

    //Put it last item
    if (!this.admin || !this.authStore.isRestrictedAdmin()) {
      this.bulkActions.push({
        content: 'Delete',
        action: (items) =>
          dialog$
            .deletePrompt(true, { itemName: items.map((i) => i.name.english).join(',') })
            .action(() => this.productsApi.delete(items.map((p) => p.id))),
      });
    }
  }

  private bulkAction(type: 'approval' | 'status', value: boolean) {
    return (items: ProductItem[]) => {
      switch (type) {
        case 'approval':
          return this.productsApi.changeApproval(
            value,
            items.map((p) => p.id),
          );
        case 'status':
          return this.productsApi.changeStatus(
            value,
            items.map((p) => p.id),
          );
        default:
          console.warn(`Unknown bulk action type: ${type}`);
          return of();
      }
    };
  }

  private getEndLayerCategories() {
    return this.productsApi.getCategories().pipe(
      map((categories: CategoryItem[]) => {
        const endLayerCategories: { value: number; label: string }[] = [];

        const findEndLayerCategories = (categories: CategoryItem[]) => {
          categories.forEach((category: CategoryItem) => {
            if (!category.subCategories || category.subCategories.length === 0) {
              // This is an end layer category (no sub-categories)
              endLayerCategories.push({
                value: category.id,
                label: category.name.english || category.name.arabic
              });
            } else {
              // Recursively check sub-categories
              findEndLayerCategories(category.subCategories);
            }
          });
        };

        findEndLayerCategories(categories);
        return endLayerCategories;
      })
    );
  }

  private initSearchControl(): void {
    this.searchControl.value$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe(() => {
      this.table.ref?.refresh();
    });
  }
}
